import { useMemo, useRef, useState, useCallback, FC } from 'react'
import styles from 'components/features/insights/InsightPost/InsightPost.styl'
import { WithPostUid } from 'interfaces/feed'
import { PostLayout } from 'components/features/pfm/PostLayout/PostLayout'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { InsightPostUserInfo } from 'components/features/insights/InsightPostUserInfo/InsightPostUserInfo'
import { formatRewardAmount } from 'helpers/formatHelpers'
import { isHighlightInsight, isModuleInsight } from 'helpers/insightHelpers'
import Badge from 'components/features/insights/Insight/Badge'
import { ExpandableAnnotation } from 'components/features/pfm/ExpandableAnnotation/ExpandableAnnotation'
import Annotation from 'components/features/insights/Insight/Annotation'
import { filterEventsFromNode, getOffset } from 'helpers/DOMHelpers'
import { InsightPostAttach } from 'components/features/insights/InsightPostAttach/InsightPostAttach'
import { FeedPostMeta } from 'components/features/socialFeed/FeedPost/FeedPostMeta'
import { createFeedPostSelector } from 'selectors/feedSelectors'
import { isMobileSelector } from 'selectors/viewportSelectors'
import Shownable from 'components/utils/Shownable'
import { insightShownActionCreator } from 'actions/insightsActions'

type InsightPostProps =
  & WithPostUid

export const InsightPost: FC<InsightPostProps> = (props) => {
  const { postUid } = props
  const feedPostSelector = useMemo(createFeedPostSelector, [])
  const post = useStoreSelector((state) => feedPostSelector(state, props))

  const {
    sponsoredPost,
    insight,
    reward,
  } = useMemo(() => {
    const sponsoredPostLocal = post && post.type === 'sponsored_post' ? post : null
    const insightLocal = sponsoredPostLocal ? sponsoredPostLocal.insight : null
    const rewardLocal = insightLocal ? insightLocal.reward : null
    return {
      sponsoredPost: sponsoredPostLocal,
      insight: insightLocal,
      reward: rewardLocal,
    }
  }, [post])

  const rootRef = useRef<HTMLDivElement>(null)
  const rewardBadgeRef = useRef<HTMLDivElement>(null)
  const [isRewardExpanded, setIsRewardExpanded] = useState(false)
  const handleRewardBadgeClick = useCallback(() => {
    if (Boolean(reward && reward.text)) {
      setIsRewardExpanded(!isRewardExpanded)
    }
  }, [setIsRewardExpanded, isRewardExpanded, reward])
  const handleCloseAnnotation = useCallback(() => setIsRewardExpanded(false), [setIsRewardExpanded])

  const rewardChild = useMemo(() => {
    if (!insight || !reward) {
      return null
    }

    const amount = formatRewardAmount(reward)

    return (
      <div ref={ rewardBadgeRef }>
        <Badge
          theme='golden'
          withChevron={ Boolean(reward.text) }
          isExpanded={ isRewardExpanded }
          onClick={ handleRewardBadgeClick }
        >
          { amount } Reward
        </Badge>
      </div>
    )
  }, [reward, insight, isRewardExpanded, handleRewardBadgeClick])

  const isMobile = useStoreSelector(isMobileSelector)

  const annotationOffset = useMemo(() => {
    return getOffset(rootRef.current, rewardBadgeRef.current)
  }, [isMobile, rootRef.current, rewardBadgeRef.current])

  const rewardAnnotationChild = useMemo(() => {
    if (!insight || !reward) {
      return null
    }

    const { text } = reward

    return (
      <ExpandableAnnotation
        isShown={ isRewardExpanded }
        offset={ annotationOffset }
        onClose={ filterEventsFromNode(rewardBadgeRef, handleCloseAnnotation) }
      >
        <Annotation
          theme='golden'
          size={ isHighlightInsight(insight) ? 'fixed' : 'responsive' }
          isPostMode
          isExpanded={ isRewardExpanded }
          isModuleInsight={ isModuleInsight(insight) }
          title='Reward Details'
          markdown={ text }
          onCloseButtonClick={ handleCloseAnnotation }
        />
      </ExpandableAnnotation>
    )
  }, [reward, insight, isRewardExpanded, annotationOffset, handleCloseAnnotation])

  const headerChild = useMemo(() => (
    <div className={ styles.userInfo }>
      <InsightPostUserInfo postUid={ postUid }>
        { rewardChild }
      </InsightPostUserInfo>
    </div>
  ), [postUid, rewardChild])

  const dispatch = useStoreDispatch()
  const handleShow = useCallback(() => {
    if (!insight) {
      return
    }

    dispatch(insightShownActionCreator(insight))
  }, [dispatch, insight])

  if (!sponsoredPost || sponsoredPost.type !== 'sponsored_post') {
    return null
  }
  return (
    <div ref={ rootRef } className={ styles.insightPost }>
      <PostLayout
        postUid={ sponsoredPost.uid }
        header={ headerChild }
      >
        <Shownable onShown={ handleShow }>
          <div className={ styles.attach }>
            <InsightPostAttach post={ sponsoredPost } />
          </div>
        </Shownable>

        <FeedPostMeta postUid={ postUid } allowShare={ false } allowFollow={ false } />

        { rewardAnnotationChild }
      </PostLayout>
    </div>
  )
}
